import { finalize, map, Observable } from 'rxjs';
import { escapeRegExp } from '../regex';

/**
 * Reads a file and returns its content as a Data URL.
 *
 * @param {File} file - The file to read.
 * @returns {Observable<string>} An observable that emits the file content as a Data URL.
 */
export function readFile(file: File): Observable<string> {
  return new Observable<string>((subscriber) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      subscriber.next(e.target?.result as string);
      subscriber.complete();
    };

    reader.onerror = (e) => {
      subscriber.error(e.target?.error);
    };
  });
}

/**
 * Loads an image from the given source URL.
 *
 * @param {string} src - The source URL of the image.
 * @returns {Observable<HTMLImageElement>} An observable that emits the loaded HTMLImageElement.
 */
export function loadImage(src: string): Observable<HTMLImageElement> {
  return new Observable<HTMLImageElement>((subscriber) => {
    const image = new Image();
    image.src = src;
    image.crossOrigin = 'anonymous';

    image.onload = () => {
      subscriber.next(image);
      subscriber.complete();
    };

    image.onerror = (error) => {
      subscriber.error(error);
    };
  });
}

/**
 * Loads an image (from a URL or File) and returns its resolution.
 *
 * @param {string | File} fileOrSrc - The image source as a URL or a File object.
 * @returns {Observable<{ width: number; height: number }>} An observable that emits an object with the image's width and height.
 */
export function loadImageResolution(
  fileOrSrc: string | File
): Observable<{ width: number; height: number }> {
  const src =
    typeof fileOrSrc === 'string' ? fileOrSrc : URL.createObjectURL(fileOrSrc);

  return loadImage(src).pipe(
    map((image) => ({
      width: image.width,
      height: image.height,
    })),
    finalize(() => {
      if (fileOrSrc !== 'string') {
        URL.revokeObjectURL(src);
      }
    })
  );
}

/**
 * Checks if a given MIME type is accepted based on the provided accept criteria.
 *
 * @param {string | null} type - The MIME type to check.
 * @param {string | string[] | null} accept - The accept criteria, as a comma-separated string or an array of strings.
 * @returns {boolean} True if the MIME type is accepted, false otherwise.
 */
export function checkMimeType(
  type: string | null,
  accept: string | string[] | null
): boolean {
  if (!accept || !type) {
    return true;
  }

  const acceptArray = Array.isArray(accept)
    ? accept
    : accept.split(',').map((item) => item.trim());
  const acceptRegexStr = acceptArray
    .map((item) => `^${escapeRegExp(item, ['*'])}$`)
    .join('|')
    .replace(/\*/g, '.*');

  const regex = new RegExp(acceptRegexStr);
  return regex.test(type);
}

/**
 * Validates whether a file's MIME type matches the accept criteria.
 *
 * @param {File} file - The file to validate.
 * @param {string | string[] | null} accept - The accept criteria for MIME types.
 * @returns {boolean} True if the file's MIME type is accepted, false otherwise.
 */
export function validateAccept(
  file: File,
  accept: string | string[] | null
): boolean {
  return checkMimeType(file?.type, accept);
}

/**
 * Collects the files carried by a clipboard event (e.g. a pasted screenshot)
 * that match the accept criteria.
 *
 * @param {ClipboardEvent} event - The paste event to read files from.
 * @param {string | string[] | null} accept - The accept criteria for MIME types.
 * @returns {File[]} The accepted files; empty when the clipboard holds none.
 */
export function getAcceptedClipboardFiles(
  event: ClipboardEvent,
  accept: string | string[] | null
): File[] {
  const items = event.clipboardData?.items;

  if (!items) {
    return [];
  }

  const files: File[] = [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    if (item.kind !== 'file') {
      continue;
    }

    const file = item.getAsFile();

    if (file && validateAccept(file, accept)) {
      files.push(file);
    }
  }

  return files;
}

/**
 * Extracts the filename from a given URL.
 *
 * @param {string} url - The URL to extract the filename from.
 * @returns {string} The extracted filename.
 */
export function getFilenameFromUrl(url: string): string {
  let cleanUrl: string;

  if (url.includes('?')) cleanUrl = url.split('?')[0];
  else cleanUrl = url;

  const decodedUrl = decodeURIComponent(cleanUrl);

  const segments = decodedUrl.split('/');

  return segments.pop() || '';
}
